// GAZDE POSIBILE PENTRU PILE — geometrie pură, fără DOM.
//
// Întrebarea: unde ar putea sta, pe nivelul ăsta, un stâlp care nu coboară la
// fundație, ci se reazemă pe o grindă a nivelului de DEDESUBT? Răspunsul e o
// porțiune de axă de grindă de dedesubt care:
//
//   1. trece pe sub un perete PLIN al nivelului curent — poligonul `Wall`
//      minus golurile lui (`Window`/`Door`): un stâlp în fereastră nu e stâlp;
//   2. stă la ≥ `capat_m` de capetele grinzii — lângă reazem grinda are deja
//      stâlpul ei, iar o forță concentrată în nod nu e transfer;
//   3. stă la ≥ `stalp_m` de orice `Column` al nivelului curent — două
//      secțiuni lipite sunt un stâlp lățit, nu doi;
//   4. rămâne, după toate scăderile, de cel puțin `minim_m` — sub atât nu
//      încape o secțiune cu acoperirile ei.
//
// Totul e o singură operație repetată: segment ∩ poligon, ca intervale pe axa
// grinzii, în metri de la capătul i. Pe intervale se face restul (reuniune,
// scădere), deci fiecare regulă e o linie, nu un caz.

import type { Pt } from "./types";
import type { StoreyPlan, StoreyShape } from "./canonical";

/** Pragurile regulilor, în metri — v. antetul. */
export const REGULI = { capat_m: 1.5, stalp_m: 0.7, minim_m: 0.8 } as const;
export type Reguli = { capat_m: number; stalp_m: number; minim_m: number };

/** O porțiune utilizabilă: capetele ei pe plan și lungimea, pe grinda `grinda`. */
export interface Gazda {
  grinda: string;
  a: Pt;
  b: Pt;
  L_m: number;
}

export type Interval = [number, number];

const EPS = 1e-9;

/** Axa unei bare (stâlp/grindă), din `axis` când documentul o scrie, altfel
 *  din inel: direcția laturii celei mai lungi, întinsă pe toată proiecția
 *  inelului, prin mijlocul lui. `null` fără geometrie. */
export function axaBarei(s: StoreyShape): [Pt, Pt] | null {
  const ax = s.axis;
  if (ax && ax.length >= 2) {
    const p = ax[0], q = ax[ax.length - 1];
    if (Math.hypot(q[0] - p[0], q[1] - p[1]) > EPS) {
      return [[p[0], p[1]], [q[0], q[1]]];
    }
  }
  const poly = s.poly ?? [];
  if (poly.length < 3) return null;
  let best = 0, ux = 1, uy = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const dx = q[0] - p[0], dy = q[1] - p[1], L = Math.hypot(dx, dy);
    if (L > best) { best = L; ux = dx / L; uy = dy / L; }
  }
  if (best <= EPS) return null;
  let tMin = Infinity, tMax = -Infinity, nSum = 0;
  for (const p of poly) {
    const t = p[0] * ux + p[1] * uy;
    tMin = Math.min(tMin, t); tMax = Math.max(tMax, t);
    nSum += -p[0] * uy + p[1] * ux;
  }
  const n = nSum / poly.length;
  // punctul = t·u + n·(−uy, ux)
  return [
    [tMin * ux - n * uy, tMin * uy + n * ux],
    [tMax * ux - n * uy, tMax * uy + n * ux],
  ];
}

function inPoligon(x: number, y: number, poly: number[][]): boolean {
  let inauntru = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y)
        && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inauntru = !inauntru;
    }
  }
  return inauntru;
}

/** Porțiunile segmentului a→b care stau ÎN poligon, ca intervale în metri de
 *  la `a`. Merge pe orice poligon simplu: se taie segmentul la fiecare muchie
 *  și se întreabă mijlocul fiecărei bucăți dacă e înăuntru. */
export function intervalePeSegment(a: Pt, b: Pt, poly: number[][]): Interval[] {
  if (poly.length < 3) return [];
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
  if (L <= EPS) return [];
  const taieturi = [0, L];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const ex = q[0] - p[0], ey = q[1] - p[1];
    const den = dx * ey - dy * ex;
    if (Math.abs(den) <= EPS) continue;          // paralele
    const s = ((p[0] - a[0]) * ey - (p[1] - a[1]) * ex) / den;   // pe segment
    const u = ((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / den;   // pe muchie
    if (s >= -EPS && s <= 1 + EPS && u >= -EPS && u <= 1 + EPS) {
      taieturi.push(Math.min(Math.max(s, 0), 1) * L);
    }
  }
  taieturi.sort((x, y) => x - y);
  const out: Interval[] = [];
  for (let i = 0; i + 1 < taieturi.length; i++) {
    const t0 = taieturi[i], t1 = taieturi[i + 1];
    if (t1 - t0 <= EPS) continue;
    const tm = (t0 + t1) / 2;
    if (inPoligon(a[0] + (dx / L) * tm, a[1] + (dy / L) * tm, poly)) {
      out.push([t0, t1]);
    }
  }
  return uneste(out);
}

/** Reuniunea unor intervale (sortate și lipite). */
export function uneste(intervale: Interval[]): Interval[] {
  const s = intervale
    .filter(([x, y]) => y - x > EPS)
    .map(([x, y]) => [x, y] as Interval)
    .sort((p, q) => p[0] - q[0]);
  const out: Interval[] = [];
  for (const iv of s) {
    const ultim = out[out.length - 1];
    if (ultim && iv[0] <= ultim[1] + EPS) ultim[1] = Math.max(ultim[1], iv[1]);
    else out.push(iv);
  }
  return out;
}

/** `baza` minus `minus`, ca intervale. */
export function scade(baza: Interval[], minus: Interval[]): Interval[] {
  let out = uneste(baza);
  for (const [m0, m1] of uneste(minus)) {
    const urm: Interval[] = [];
    for (const [b0, b1] of out) {
      if (m1 <= b0 + EPS || m0 >= b1 - EPS) { urm.push([b0, b1]); continue; }
      if (m0 > b0 + EPS) urm.push([b0, m0]);
      if (m1 < b1 - EPS) urm.push([m1, b1]);
    }
    out = urm;
  }
  return out;
}

/** Centrul și «raza» unui stâlp: jumătatea celei mai mari întinderi a
 *  inelului, ca distanța până la el să fie de la FAȚA lui, nu de la centru. */
function stalpul(c: StoreyShape): { x: number; y: number; r: number } | null {
  const poly = c.poly ?? [];
  if (poly.length >= 3) {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const [x, y] of poly) {
      xMin = Math.min(xMin, x); xMax = Math.max(xMax, x);
      yMin = Math.min(yMin, y); yMax = Math.max(yMax, y);
    }
    return { x: (xMin + xMax) / 2, y: (yMin + yMax) / 2,
             r: Math.max(xMax - xMin, yMax - yMin) / 2 };
  }
  if (c.cx != null && c.cy != null) return { x: c.cx, y: c.cy, r: 0 };
  return null;
}

/** Porțiunile axelor grinzilor de DEDESUBT pe care ar putea sta o pilă a
 *  nivelului CURENT — v. antetul pentru cele patru reguli. */
export function gazdePosibile(
  curent: StoreyPlan,
  dedesubt: StoreyPlan,
  reguli: Reguli = REGULI
): Gazda[] {
  const pereti = (curent.walls ?? []).filter((w) => (w.poly ?? []).length >= 3);
  const goluri = (curent.openings ?? []).filter((o) => (o.poly ?? []).length >= 3);
  const stalpi = (curent.columns ?? []).map(stalpul)
    .filter((c): c is { x: number; y: number; r: number } => c !== null);
  if (pereti.length === 0) return [];
  const out: Gazda[] = [];
  for (const g of dedesubt.beams ?? []) {
    const axa = axaBarei(g);
    if (!axa) continue;
    const [a, b] = axa;
    const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
    if (L <= 2 * reguli.capat_m + reguli.minim_m) continue;
    const ux = dx / L, uy = dy / L;
    // 1. sub perete plin
    const subPereti = uneste(pereti.flatMap((w) => intervalePeSegment(a, b, w.poly)));
    if (subPereti.length === 0) continue;
    const subGoluri = goluri.flatMap((o) => intervalePeSegment(a, b, o.poly));
    // 2. departe de capetele grinzii
    const capete: Interval[] = [[0, reguli.capat_m], [L - reguli.capat_m, L]];
    // 3. departe de stâlpii nivelului curent
    const langaStalpi: Interval[] = [];
    for (const c of stalpi) {
      const px = c.x - a[0], py = c.y - a[1];
      const t = px * ux + py * uy;
      const d = Math.abs(-px * uy + py * ux);
      const r = reguli.stalp_m + c.r;
      if (d < r) {
        const h = Math.sqrt(r * r - d * d);
        langaStalpi.push([t - h, t + h]);
      }
    }
    // 4. ce rămâne, dacă e destul
    for (const [t0, t1] of scade(scade(scade(subPereti, subGoluri), capete), langaStalpi)) {
      if (t1 - t0 + EPS < reguli.minim_m) continue;
      out.push({
        grinda: g.eid,
        a: [a[0] + ux * t0, a[1] + uy * t0],
        b: [a[0] + ux * t1, a[1] + uy * t1],
        L_m: t1 - t0,
      });
    }
  }
  return out;
}
